import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Alert, App, Space, Typography } from 'antd';
import { LockOutlined, CheckCircleOutlined, MailOutlined, ClockCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { getErrorMessage } from '../../utils/errorMessage';
import PasswordResetForm from './PasswordResetForm';
import { usePasswordResetRequest } from '../../hooks/usePasswordResetRequest';

const { Title, Text } = Typography;

interface ResetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

const PasswordResetPage: React.FC = () => {
  const { message } = App.useApp();
  const [resetForm] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0: request, 1: reset, 2: success

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      setStep(1); // トークンがある場合はリセット画面に直接進む
    }
  }, [token]);

  const { requestPasswordReset } = usePasswordResetRequest({
    onSuccess: () => setStep(2),
  });

  // パスワードリセット実行
  const resetPasswordMutation = trpc.user.resetPassword.useMutation({
    onSuccess: () => {
      setStep(2);
    },
    onError: (error: unknown) => {
      message.error(getErrorMessage(error, 'パスワードリセットに失敗しました'));
    },
  });

  const handleResetSubmit = async (values: ResetPasswordFormValues) => {
    if (!token) return;

    await resetPasswordMutation.mutateAsync({
      token,
      password: values.password,
    });
  };

  // ステップ0: パスワードリセット要求
  if (step === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}>
        <Card style={{ width: 450, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <LockOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
              <Title level={2} style={{ marginBottom: 8 }}>パスワードをお忘れですか？</Title>
              <Text type="secondary">
                メールアドレスを入力してください。パスワードリセットのリンクをお送りします。
              </Text>
            </div>

            <PasswordResetForm
              onSubmit={async (email) => {
                await requestPasswordReset(email);
              }}
            />

            <div style={{ textAlign: 'center' }}>
              <Button type="link" onClick={() => navigate('/login')}>
                ログインページに戻る
              </Button>
            </div>
          </Space>
        </Card>
      </div>
    );
  }

  // ステップ1: パスワードリセット
  if (step === 1) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}>
        <Card style={{ width: 450, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <LockOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
              <Title level={2} style={{ marginBottom: 8 }}>新しいパスワードを設定</Title>
              <Text type="secondary">
                新しいパスワードを入力してください。
              </Text>
            </div>

            <Form
              form={resetForm}
              layout="vertical"
              onFinish={handleResetSubmit}
            >
              <Form.Item
                name="password"
                label="新しいパスワード"
                rules={[
                  { required: true, message: 'パスワードを入力してください' },
                  { min: 8, message: 'パスワードは8文字以上で入力してください' },
                  { max: 100, message: 'パスワードは100文字以内で入力してください' },
                  {
                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                    message: 'パスワードは大文字・小文字・数字を含む必要があります',
                  },
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="安全なパスワードを入力"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="パスワード（確認）"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'パスワードを再入力してください' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('パスワードが一致しません'));
                    },
                  }),
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="同じパスワードを再入力"
                  size="large"
                />
              </Form.Item>

              <Alert
                message="パスワードの要件"
                description={
                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    <li>8文字以上100文字以内</li>
                    <li>大文字、小文字、数字を含む</li>
                    <li>特殊文字の使用を推奨</li>
                  </ul>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              {resetPasswordMutation.error && (
                <Alert
                  message="エラー"
                  description={resetPasswordMutation.error.message}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <Form.Item style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={resetPasswordMutation.isPending}
                  size="large"
                  block
                >
                  パスワードを変更
                </Button>
              </Form.Item>
            </Form>

            <div style={{ textAlign: 'center' }}>
              <Button type="link" onClick={() => navigate('/login')}>
                ログインページに戻る
              </Button>
            </div>
          </Space>
        </Card>
      </div>
    );
  }

  // ステップ2: 成功画面
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      <Card style={{ width: 500, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 成功アイコンとタイトル */}
          <div style={{ textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '16px' }} />
            <Title level={2} style={{ marginBottom: 8, color: '#52c41a' }}>
              {token ? 'パスワードを変更しました！' : 'リセットメールを送信しました！'}
            </Title>
          </div>

          {/* メッセージ */}
          {token ? (
            <Alert
              message="変更完了"
              description={
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text>パスワードが正常に変更されました。</Text>
                  <Text>新しいパスワードでログインできます。</Text>
                </Space>
              }
              type="success"
              showIcon
            />
          ) : (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Alert
                icon={<MailOutlined />}
                message="メールを送信しました"
                description={
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text>
                      パスワードリセットのリンクをメールでお送りしました。
                    </Text>
                    <Text strong style={{ color: '#1890ff' }}>
                      受信トレイをご確認ください。
                    </Text>
                  </Space>
                }
                type="success"
                showIcon
              />

              {/* リンク有効期限の案内 */}
              <Alert
                icon={<ClockCircleOutlined />}
                message="リンクの有効期限"
                description={
                  <Space direction="vertical" size="small">
                    <Text>リセットリンクの有効期限は<strong>1時間</strong>です。</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      期限が切れた場合は、再度リセット申請を行ってください。
                    </Text>
                  </Space>
                }
                type="warning"
                showIcon
              />

              {/* メールが届かない場合の案内 */}
              <Alert
                icon={<QuestionCircleOutlined />}
                message="メールが届かない場合"
                description={
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text>以下をご確認ください：</Text>
                    <ul style={{ marginTop: 8, paddingLeft: 20, marginBottom: 0 }}>
                      <li>
                        <Text>迷惑メール（スパム）フォルダをご確認ください</Text>
                      </li>
                      <li>
                        <Text>メールアドレスが正しいかご確認ください</Text>
                      </li>
                      <li>
                        <Text>メール受信には最大3分程度かかる場合があります</Text>
                      </li>
                      <li>
                        <Text>
                          それでも届かない場合は、
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: '0 4px' }}
                            onClick={() => setStep(0)}
                          >
                            こちら
                          </Button>
                          から再送信してください
                        </Text>
                      </li>
                    </ul>
                  </Space>
                }
                type="info"
                showIcon
              />
            </Space>
          )}

          {/* ログインページに戻るボタン */}
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button
              type="primary"
              size="large"
              onClick={() => navigate('/login')}
              style={{ minWidth: 200 }}
            >
              ログインページへ
            </Button>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default PasswordResetPage;
